import { z } from 'zod';

/**
 * The office: floors laid out as rooms, walls and furniture in world pixels. One floor is one
 * JSON layout, shared by the server (validation, who hears whom), the renderer and the editor.
 */

/** World size limits in pixels and the grid furniture snaps to. */
export const OFFICE_WORLD = { minSize: 800, maxSize: 6000, grid: 40 } as const;
/** Collision grid cell size in pixels. */
export const OFFICE_CELL = 16;
/** Drawn radius of a person's circle. */
export const PERSON_RADIUS = 26;
/** Collision radius of a person (smaller than drawn so people fit through doors comfortably). */
export const PERSON_BODY = 16;
/** Walking speed in pixels per second. */
export const WALK_SPEED = 240;
export const DOOR_WIDTH = 88;
export const WALL_THICKNESS = 8;

/**
 * Room kinds. `office`, `meeting` and `quiet` have walls and isolate audio: inside, everyone hears
 * everyone and nothing crosses the walls. `open`, `team` and `lounge` are labelled areas with no walls
 * by default. `stage` is a broadcast area (Phase C).
 */
export const ROOM_KINDS = ['open', 'team', 'lounge', 'office', 'meeting', 'quiet', 'stage'] as const;
export type RoomKind = (typeof ROOM_KINDS)[number];

export const ROOM_KIND_INFO: Record<RoomKind, { label: string; walls: boolean; isolated: boolean }> = {
  open: { label: 'Open area', walls: false, isolated: false },
  team: { label: 'Team area', walls: false, isolated: false },
  lounge: { label: 'Lounge', walls: false, isolated: false },
  office: { label: 'Office', walls: true, isolated: true },
  meeting: { label: 'Meeting room', walls: true, isolated: true },
  quiet: { label: 'Quiet room', walls: true, isolated: true },
  stage: { label: 'Stage', walls: false, isolated: false },
};

export const FURNITURE_KINDS = [
  'desk',
  'chair',
  'sofa',
  'armchair',
  'table-round',
  'table-long',
  'plant',
  'rug',
  'whiteboard',
  'screen',
  'bookshelf',
  'counter',
  'coffee',
  'lamp',
  'sign',
  'image',
  'sticky',
  'portal',
] as const;
export type FurnitureKind = (typeof FURNITURE_KINDS)[number];

/** Default size and whether people walk around the item (solid) or over it (rugs, signs). */
export const FURNITURE_INFO: Record<FurnitureKind, { label: string; w: number; h: number; solid: boolean }> =
  {
    desk: { label: 'Desk', w: 120, h: 60, solid: true },
    chair: { label: 'Chair', w: 40, h: 40, solid: false },
    sofa: { label: 'Sofa', w: 160, h: 64, solid: true },
    armchair: { label: 'Armchair', w: 64, h: 64, solid: true },
    'table-round': { label: 'Round table', w: 120, h: 120, solid: true },
    'table-long': { label: 'Long table', w: 280, h: 100, solid: true },
    plant: { label: 'Plant', w: 48, h: 48, solid: true },
    rug: { label: 'Rug', w: 240, h: 160, solid: false },
    whiteboard: { label: 'Whiteboard', w: 160, h: 24, solid: true },
    screen: { label: 'Screen', w: 120, h: 20, solid: true },
    bookshelf: { label: 'Bookshelf', w: 120, h: 36, solid: true },
    counter: { label: 'Counter', w: 240, h: 60, solid: true },
    coffee: { label: 'Coffee machine', w: 40, h: 40, solid: true },
    lamp: { label: 'Lamp', w: 32, h: 32, solid: true },
    sign: { label: 'Sign', w: 160, h: 40, solid: false },
    image: { label: 'Picture', w: 160, h: 120, solid: false },
    sticky: { label: 'Sticky note', w: 96, h: 96, solid: false },
    /** Step on it to go to another floor (`url` holds the floor id). */
    portal: { label: 'Stairs to another floor', w: 96, h: 64, solid: false },
  };

/** Furniture that can carry a link or text, and what the editor asks for. */
export const FURNITURE_FIELDS: Partial<
  Record<FurnitureKind, { label?: boolean; url?: 'link' | 'image' | 'floor' }>
> = {
  sign: { label: true, url: 'link' },
  sticky: { label: true },
  image: { url: 'image' },
  whiteboard: { url: 'link' },
  screen: { url: 'link' },
  portal: { label: true, url: 'floor' },
  desk: { label: true },
};

export const DOOR_SIDES = ['top', 'right', 'bottom', 'left'] as const;
export type DoorSide = (typeof DOOR_SIDES)[number];

const id = z.string().min(1).max(64);
const coord = z.number().int().min(-20_000).max(20_000);
const size = z.number().int().min(16).max(OFFICE_WORLD.maxSize);
const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);

export const roomSchema = z.object({
  id,
  name: z.string().trim().min(1).max(60),
  kind: z.enum(ROOM_KINDS),
  x: coord,
  y: coord,
  w: size,
  h: size,
  /** Overrides the kind's default: walls drawn and blocking. */
  walls: z.boolean().optional(),
  /** The room uses explicit layout wall segments, rather than an automatic rectangle. */
  boundary: z.literal('explicit').optional(),
  color: hex.optional(),
  /** Where the door sits on a walled room: the side and the distance along it from its start. */
  door: z
    .object({ side: z.enum(DOOR_SIDES), offset: z.number().int().min(0).max(OFFICE_WORLD.maxSize) })
    .optional(),
  capacity: z.number().int().min(1).max(500).nullable().optional(),
  isPrivate: z.boolean().optional(),
  /** Assigned office owner; retains entry and controls its lock even when away. */
  ownerId: z.string().min(1).max(64).nullable().optional(),
  /** Team areas: the team that sits here. */
  teamId: z.string().max(64).nullable().optional(),
});
export type OfficeRoom = z.infer<typeof roomSchema>;

export const furnitureSchema = z.object({
  id,
  kind: z.enum(FURNITURE_KINDS),
  x: coord,
  y: coord,
  w: size,
  h: size,
  rotation: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]).default(0),
  color: hex.optional(),
  label: z.string().trim().max(80).optional(),
  /** Pictures and link signs. */
  url: z.string().max(2000).optional(),
  /** Overrides the kind's default. */
  solid: z.boolean().optional(),
  /** Desks: whose desk it is. */
  userId: z.string().max(64).nullable().optional(),
});
export type OfficeFurniture = z.infer<typeof furnitureSchema>;

export const wallSchema = z.object({ id, x1: coord, y1: coord, x2: coord, y2: coord });
export type OfficeWall = z.infer<typeof wallSchema>;

export const FLOOR_PATTERNS = ['plain', 'wood', 'tiles', 'carpet'] as const;
export type FloorPattern = (typeof FLOOR_PATTERNS)[number];

/** The material sets a floor can be drawn in: timber, upholstery, rugs and walls. The colours live in the web renderer. */
export const OFFICE_PALETTES = [
  'oak',
  'coastal',
  'walnut',
  'forest',
  'midnight',
  'rose',
  'sand',
  'lavender',
  'graphite',
  'terracotta',
  'nordic',
  'emerald',
] as const;
export type OfficePalette = (typeof OFFICE_PALETTES)[number];

export const officeLayoutSchema = z.object({
  version: z.literal(1),
  width: z.number().int().min(OFFICE_WORLD.minSize).max(OFFICE_WORLD.maxSize),
  height: z.number().int().min(OFFICE_WORLD.minSize).max(OFFICE_WORLD.maxSize),
  floor: z.object({ color: hex, pattern: z.enum(FLOOR_PATTERNS).default('plain') }),
  /** Which materials the floor is drawn in; Warm oak when unset. */
  palette: z.enum(OFFICE_PALETTES).optional(),
  spawn: z.object({ x: coord, y: coord }),
  rooms: z.array(roomSchema).max(200),
  items: z.array(furnitureSchema).max(2000),
  walls: z.array(wallSchema).max(500).default([]),
  /** Authored connected design; geometry is saved alongside it for server collision. */
  design: z
    .object({
      kind: z.literal('connected-3d'),
      offices: z
        .array(
          z.object({
            id,
            preset: z.enum(['executive', 'designer', 'sales', 'custom']),
            name: z.string().trim().min(1).max(60),
            items: z.array(furnitureSchema).max(100),
          }),
        )
        .max(18),
      roomIds: z.record(z.string(), id).default({}),
      upholstery: z
        .object({ chair: hex.nullable(), sofa: hex.nullable(), armchair: hex.nullable() })
        .optional(),
    })
    .optional(),
});
export type OfficeLayout = z.infer<typeof officeLayoutSchema>;

/** How far people hear each other in open areas. */
export const AUDIO_RANGES = ['quiet', 'normal', 'loud', 'floor'] as const;
export type AudioRange = (typeof AUDIO_RANGES)[number];
export const AUDIO_RANGE_PX: Record<AudioRange, number> = {
  quiet: 170,
  normal: 280,
  loud: 420,
  floor: 1_000_000,
};

export const floorSettingsSchema = z.object({
  audioRange: z.enum(AUDIO_RANGES).default('normal'),
});
export type OfficeFloorSettings = z.infer<typeof floorSettingsSchema>;

export const OFFICE_TEMPLATES = ['office', 'lounge', 'event'] as const;
export type OfficeTemplate = (typeof OFFICE_TEMPLATES)[number];

export const createFloorSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  template: z.enum(OFFICE_TEMPLATES).default('office'),
});
export type CreateFloorInput = z.infer<typeof createFloorSchema>;

export const updateFloorSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  settings: floorSettingsSchema.partial().optional(),
  layout: officeLayoutSchema.optional(),
  /** Fractional index among the workspace's floors. */
  position: z.string().min(1).max(64).optional(),
});
export type UpdateFloorInput = z.infer<typeof updateFloorSchema>;

export interface OfficeFloorDTO {
  id: string;
  name: string;
  position: string;
  template: OfficeTemplate;
  settings: OfficeFloorSettings;
  layout: OfficeLayout;
  /** The floor's saved chat channel, when it has one. */
  channelId: string | null;
  /** What the viewer may do: `view` enters, `edit` lays the floor out (a floor manager), `full` also shares it. */
  access: 'view' | 'comment' | 'edit' | 'full';
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

/** A floor in the lobby list, without its layout. */
export interface OfficeFloorSummaryDTO extends Omit<OfficeFloorDTO, 'layout'> {
  width: number;
  height: number;
  roomCount: number;
}

export interface OfficeChangedEventData {
  floorId: string;
  op: 'created' | 'updated' | 'deleted' | 'restored';
}

/** Who is on a floor right now. Published by the floor whenever someone comes or goes. */
export interface OfficePresenceEventData {
  floorId: string;
  userIds: string[];
}

/**
 * Who can use the office at all. While it is being finished, only platform admins see it: the
 * navigation item, the routes, the API and the floor sockets all check this one switch. Opening it to
 * everyone is a change here (for example `return true`, or a per-workspace setting).
 */
export function officeOpenTo(actor: { platformAdmin?: boolean } | null | undefined): boolean {
  return Boolean(actor?.platformAdmin);
}
